/**
 * Deep link / share URL generation for DUEL profiles and workouts.
 */

const WEB_BASE = 'https://douevenlift.com';

/**
 * Append UTM attribution parameters to a URL.
 */
function appendUtm(url: string, campaign: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}utm_source=duel&utm_medium=share&utm_campaign=${campaign}`;
}

/**
 * Generate a shareable profile URL.
 */
export function generateProfileShareLink(handle: string): string {
  return appendUtm(`${WEB_BASE}/u/${encodeURIComponent(handle)}`, 'profile');
}

/**
 * Generate a shareable workout URL.
 */
export function generateWorkoutShareLink(workoutId: string): string {
  return `${WEB_BASE}/workout/${encodeURIComponent(workoutId)}`;
}

/**
 * OG meta tag data for server-side rendering or head injection.
 */
export interface OGMetaTags {
  title: string;
  description: string;
  url: string;
  image?: string;
  type: 'profile' | 'article';
}

export function getProfileOGTags(
  handle: string,
  displayName: string,
  strengthScore?: number | null,
  avatarUrl?: string | null
): OGMetaTags {
  const score = strengthScore ? ` | Strength: ${Math.round(strengthScore)}` : '';
  return {
    title: `${displayName} (@${handle}) — DUEL`,
    description: `${displayName} on DUEL — Do U Even Lift${score}. Challenge them to a duel.`,
    url: generateProfileShareLink(handle),
    image: avatarUrl ?? undefined,
    type: 'profile',
  };
}

/**
 * Generate a shareable duel invite URL.
 */
export function generateDuelInviteShareLink(code: string): string {
  return appendUtm(`${WEB_BASE}/duel/invite/${encodeURIComponent(code)}`, 'duel_invite');
}

/**
 * Generate a shareable duel URL.
 */
export function generateDuelShareLink(duelId: string): string {
  return appendUtm(`${WEB_BASE}/duel/${encodeURIComponent(duelId)}`, 'duel_invite');
}

/**
 * Generate share text for a completed duel result.
 */
export function getDuelShareText(duelId: string, isWinner: boolean): string {
  const link = generateDuelShareLink(duelId);
  if (isWinner) {
    return `I just won a strength duel on DUEL! 💪⚔️ Check it out: ${link}`;
  }
  return `Check out this duel on DUEL! ⚔️ ${link}`;
}

/**
 * Generate a shareable gym join URL.
 */
export function generateGymJoinShareLink(code: string): string {
  return appendUtm(`${WEB_BASE}/gym/${encodeURIComponent(code)}`, 'gym_join');
}

export function getWorkoutOGTags(
  workoutId: string,
  displayName: string,
  exerciseCount: number,
  totalVolume?: number
): OGMetaTags {
  const vol = totalVolume ? ` | ${Math.round(totalVolume).toLocaleString()} lbs moved` : '';
  return {
    title: `${displayName}'s Workout — DUEL`,
    description: `${exerciseCount} exercises${vol}. Track your lifts on DUEL.`,
    url: generateWorkoutShareLink(workoutId),
    type: 'article',
  };
}
